'use client';

import { useState } from 'react';
import { Bell, Loader2 } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { useIsMobile } from '@/hooks/use-mobile';
import { cn } from '@/lib/utils';
import { Notification } from '@/types/notifications';

import NotificationBadge from './notification-badge';
import { NotificationItem } from './notification-item';
import { useNotificationContext } from './notification-provider';

type NotificationPopoverButtonProps = {
  triggerClassName?: string;
  iconClassName?: string;
};

const DynamicNotificationPopoverButton = dynamic(
  () => Promise.resolve(NotificationPopoverButtonClient),
  {
    ssr: false,
    loading: () => <Skeleton className='size-10 rounded-full' />,
  },
);

function NotificationPopoverButtonClient({
  triggerClassName,
  iconClassName,
}: NotificationPopoverButtonProps) {
  const [isOpen, setIsOpen] = useState(false);
  const isMobile = useIsMobile();
  const router = useRouter();

  const {
    notifications,
    getCounts,
    markAsRead,
    markAllAsRead,
    removeNotification,
    isLoading,
    isLoadingMore,
    hasMore,
    loadMoreRef,
    setScrollContainerRef,
  } = useNotificationContext();

  const { all: count } = getCounts();

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open && count > 0) {
      void markAllAsRead();
    }
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read) {
      markAsRead(notification._id);
    }

    if (notification.link) {
      setIsOpen(false);
      router.push(notification.link);
    }
  };

  const handleDeleteNotification = async (notification: Notification) => {
    await removeNotification(notification._id);
  };

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        {isMobile ? (
          <button className={cn('relative p-2', triggerClassName)}>
            <Bell className={cn('size-6 text-brand-green', iconClassName)} />
            <NotificationBadge
              className='absolute -right-1 -top-1'
              type='all'
            />
          </button>
        ) : (
          <Button
            className={cn('relative', triggerClassName)}
            variant='header-icon'
            size='icon'
          >
            <Bell className={cn('text-brand-green', iconClassName)} />
            <NotificationBadge
              className='absolute -right-1 -top-1'
              type='all'
            />
          </Button>
        )}
      </PopoverTrigger>
      <PopoverContent
        className={cn(
          'mx-0 p-0 shadow-lg',
          isMobile ? 'w-[calc(100vw-2rem)] max-w-96' : 'w-96',
        )}
        align='end'
        side='bottom'
        sideOffset={8}
        alignOffset={isMobile ? -8 : 0}
        avoidCollisions={true}
        collisionPadding={16}
      >
        <div className='flex w-full flex-col'>
          <div className='flex flex-wrap items-center justify-between gap-2 border-b px-4 py-3'>
            <div className='flex items-center gap-2'>
              <h2 className='py-0 text-base font-normal'>Notifications</h2>
              <NotificationBadge
                className=''
                type='all'
                size='notification-xl'
              />
            </div>
          </div>

          {isLoading ? (
            <div className='flex items-center justify-center py-8'>
              <Loader2 className='size-6 animate-spin text-brand-green' />
            </div>
          ) : notifications?.length === 0 ? (
            <div className='my-10 flex flex-col items-center justify-center gap-2 px-4 text-center'>
              <p className='text-sm font-medium text-muted-foreground'>
                No notifications yet.
              </p>
              <p className='text-[13px] text-muted-foreground/90'>
                You&apos;ll see messages, mentions, and updates here.
              </p>
            </div>
          ) : (
            <div
              ref={setScrollContainerRef}
              className={cn(
                'scrollbar-thin flex flex-col overflow-y-auto',
                isMobile ? 'max-h-[70dvh]' : 'max-h-[600px]',
              )}
            >
              {notifications.map((notification) => (
                <div
                  key={notification._id}
                  className={cn(
                    'flex w-full cursor-pointer items-start gap-3 border-b px-3 py-4 transition-colors last:border-b-0 hover:bg-muted-foreground/5 focus:bg-muted-foreground/5',
                    !notification.read &&
                      'bg-brand-soft/25 hover:bg-brand-soft/50 focus:bg-brand-soft/50',
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <NotificationItem
                    notification={notification}
                    onDelete={handleDeleteNotification}
                  />
                </div>
              ))}

              {hasMore && (
                <div
                  ref={loadMoreRef}
                  className='flex items-center justify-center py-4'
                >
                  {isLoadingMore && (
                    <Loader2 className='size-4 animate-spin text-brand-green' />
                  )}
                </div>
              )}

              {!hasMore && notifications.length > 0 && (
                <div className='h-px w-full' />
              )}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default DynamicNotificationPopoverButton;
